// Copyright (c) 2025 Audere, All Rights Reserved

import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  SwipeableDrawer,
} from "@mui/material";
import { useCallback, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";

import BottomBasketContent from "./BottomBasketContent";
import type { BottomBasketProps } from "../types/projects";
import { theme } from "../../theme";
import { createStyleSheet } from "../utils/styles";
import { getRDTName } from "../utils/string";
import imagePlaceholder from "../../assets/image_placeholder.png";

const BottomBasket = (props: BottomBasketProps) => {
  const { testItems, onContinue, onRemove, onCancel } = props;

  const [isOpen, setIsOpen] = useState(false);

  const handleRemove = useCallback(
    (id: string) => {
      onRemove(id);
    },
    [onRemove]
  );
  const toggleBottomBasket = useCallback(() => {
    setIsOpen(!isOpen);
  }, [setIsOpen, isOpen]);
  const handleBasketClick = useCallback(() => {
    if (testItems?.length) {
      toggleBottomBasket();
    }
  }, [testItems, toggleBottomBasket]);

  return (
    <>
      {!isOpen && (
        <Box sx={styles.basketHeader}>
          <BottomBasketContent
            testsCount={testItems.length}
            onClick={handleBasketClick}
            onContinueClick={onContinue}
            isOpen={isOpen}
            onCancel={onCancel}
          />
        </Box>
      )}
      <SwipeableDrawer
        anchor="bottom"
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        disableSwipeToOpen={false}
        ModalProps={{
          keepMounted: true,
        }}
        slotProps={{
          backdrop: {
            sx: { backgroundColor: "transparent" },
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <BottomBasketContent
            testsCount={testItems.length}
            onClick={toggleBottomBasket}
            onContinueClick={onContinue}
            isOpen={isOpen}
            onCancel={onCancel}
          />
          <List>
            {testItems.map(test => (
              <ListItem
                sx={styles.listItemContainer}
                key={test.rdt_uid}
                secondaryAction={
                  <IconButton
                    edge="end"
                    onClick={() => handleRemove(test.rdt_uid)}
                    aria-label="remove test"
                  >
                    <CloseIcon color="primary" fontSize="large" />
                  </IconButton>
                }
              >
                <ListItemAvatar>
                  <Box
                    component="img"
                    src={test.example_image_url ?? imagePlaceholder}
                    sx={styles.listItemImg}
                    alt="RDT Image"
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={getRDTName(test)}
                  slotProps={{ primary: { variant: "h6" } }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </SwipeableDrawer>
    </>
  );
};

export default BottomBasket;

const styles = createStyleSheet({
  basketHeader: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
  },
  listItemContainer: { px: 0, py: 2, borderBottom: 1, borderBottomColor: "grey.300" },
  listItemImg: {
    height: theme.spacing(7),
    width: theme.spacing(7),
    objectFit: "contain",
    borderRadius: "12px",
    mr: 3,
  },
});
